(() => {
  class LogoCookieBuilder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-lcb-form]');
      if (!this.form) return;

      this.currency = root.dataset.currency || 'USD';
      this.ribbonUpcharge = this.toMoney(root.dataset.ribbonUpcharge);
      this.borderUpcharge = this.toMoney(root.dataset.borderUpcharge);
      this.turnaroundDays = {
        small: Math.max(1, parseInt(root.dataset.turnaroundSmall, 10) || 5),
        medium: Math.max(1, parseInt(root.dataset.turnaroundMedium, 10) || 6),
        large: Math.max(1, parseInt(root.dataset.turnaroundLarge, 10) || 7),
        xl: Math.max(1, parseInt(root.dataset.turnaroundXl, 10) || 10)
      };
      this.tiers = this.readTiers();

      this.qtyInput = root.querySelector('[data-lcb-qty]');
      this.qtyRange = root.querySelector('[data-lcb-range]');
      this.ribbonColorWrap = root.querySelector('[data-lcb-ribbon-color-wrap]');
      this.ribbonColor = root.querySelector('[data-lcb-ribbon-color]');
      this.borderColorWrap = root.querySelector('[data-lcb-border-color-wrap]');
      this.borderColor = root.querySelector('[data-lcb-border-color]');
      this.fileInput = root.querySelector('[data-lcb-file]');
      this.fileName = root.querySelector('[data-lcb-file-name]');
      this.atcLabel = root.querySelector('[data-lcb-atc-label]');

      this.privateUnit = root.querySelector('[data-lcb-private-unit]');
      this.privateUnitCents = root.querySelector('[data-lcb-private-unit-cents]');
      this.privateLine = root.querySelector('[data-lcb-private-line]');
      this.privateTier = root.querySelector('[data-lcb-private-tier]');
      this.privateTurnaround = root.querySelector('[data-lcb-private-turnaround]');
      this.deliveryDate = root.querySelector('[data-lcb-delivery-date]');

      this.bindGallery();
      this.bindInputs();
      this.bindTiers();
      this.bindFile();
      this.configureRange();
      this.update();
    }

    toMoney(value) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }

    readTiers() {
      const node = this.root.querySelector('[data-lcb-tiers]');
      if (!node) return [{ min: 1, small: 0, large: 0 }];
      try {
        const parsed = JSON.parse(node.textContent).map((tier) => ({
          min: Math.max(1, parseInt(tier.min, 10) || 1),
          small: this.toMoney(tier.small),
          large: this.toMoney(tier.large)
        }));
        return parsed.sort((a, b) => a.min - b.min);
      } catch (error) {
        console.error('Logo cookie pricing tiers could not be read.', error);
        return [{ min: 1, small: 0, large: 0 }];
      }
    }

    bindGallery() {
      const main = this.root.querySelector('[data-lcb-main-image]');
      if (!main) return;
      this.root.querySelectorAll('[data-lcb-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          const src = button.dataset.src;
          const alt = button.dataset.alt || '';
          if (src) main.src = src;
          main.alt = alt;
          this.root.querySelectorAll('[data-lcb-thumb]').forEach((thumb) => thumb.classList.remove('is-active'));
          button.classList.add('is-active');
        });
      });
    }

    bindInputs() {
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-lcb-option]')) this.update();
      });

      if (this.qtyRange) {
        this.qtyRange.addEventListener('input', () => {
          const index = Math.max(0, Math.min(this.tiers.length - 1, parseInt(this.qtyRange.value, 10) || 0));
          const tier = this.tiers[index];
          if (tier && this.qtyInput) this.qtyInput.value = tier.min;
          this.update();
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => {
          const qty = parseInt(this.qtyInput.value, 10);
          if (!Number.isFinite(qty)) return;
          this.update();
        });
        this.qtyInput.addEventListener('blur', () => {
          const qty = this.quantity();
          this.qtyInput.value = qty;
          this.update();
        });
      }

      this.form.addEventListener('submit', () => this.update());
    }

    bindTiers() {
      this.root.querySelectorAll('[data-lcb-tier-select]').forEach((button) => {
        button.addEventListener('click', () => {
          const qty = Math.max(this.minimumQty(), parseInt(button.dataset.min, 10) || this.minimumQty());
          if (this.qtyInput) this.qtyInput.value = qty;
          this.update();
        });
      });
    }

    bindFile() {
      if (!this.fileInput || !this.fileName) return;
      this.fileInput.addEventListener('change', () => {
        const file = this.fileInput.files && this.fileInput.files[0];
        this.fileName.textContent = file ? file.name : 'No file selected';
      });
    }

    configureRange() {
      if (!this.qtyInput || !this.qtyRange || !this.tiers.length) return;
      const min = this.minimumQty();
      this.qtyInput.min = min;
      if (!this.qtyInput.value || parseInt(this.qtyInput.value, 10) < min) this.qtyInput.value = min;
      this.qtyRange.min = 0;
      this.qtyRange.max = Math.max(0, this.tiers.length - 1);
      this.qtyRange.step = 1;
      const minLabel = this.root.querySelector('[data-lcb-range-min]');
      const maxLabel = this.root.querySelector('[data-lcb-range-max]');
      if (minLabel) minLabel.textContent = this.cookieCountLabel(this.tiers[0].min);
      if (maxLabel) maxLabel.textContent = `${this.formatQty(this.tiers[this.tiers.length - 1].min)}+ cookies`;
    }

    addBusinessDays(startDate, businessDays) {
      const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      let added = 0;
      while (added < businessDays) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return date;
    }

    localISODate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    updateDeliveryDate(businessDays) {
      if (!this.deliveryDate) return;
      const earliest = this.addBusinessDays(new Date(), businessDays);
      const earliestISO = this.localISODate(earliest);
      this.deliveryDate.min = earliestISO;

      // If quantity increases and the previously chosen date is now too early,
      // clear it instead of silently changing the customer's requested date.
      if (this.deliveryDate.value && this.deliveryDate.value < earliestISO) {
        this.deliveryDate.value = '';
      }

      const earliestLabel = this.root.querySelector('[data-lcb-earliest-date]');
      if (earliestLabel) {
        try {
          earliestLabel.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }).format(earliest);
        } catch (_) {
          earliestLabel.textContent = earliestISO;
        }
      }
    }

    minimumQty() {
      return this.tiers.length ? this.tiers[0].min : 1;
    }

    quantity() {
      const raw = parseInt(this.qtyInput ? this.qtyInput.value : this.minimumQty(), 10);
      return Math.max(this.minimumQty(), Number.isFinite(raw) ? raw : this.minimumQty());
    }

    selected(name, fallback) {
      const checked = this.form.querySelector(`[name="properties[${name}]"]:checked`);
      return checked ? checked.value : fallback;
    }

    currentTier(qty) {
      let chosen = this.tiers[0] || { min: 1, small: 0, large: 0 };
      this.tiers.forEach((tier) => {
        if (qty >= tier.min) chosen = tier;
      });
      return chosen;
    }

    currentTierIndex(qty) {
      const tier = this.currentTier(qty);
      return Math.max(0, this.tiers.findIndex((item) => item.min === tier.min));
    }

    formatQty(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { maximumFractionDigits: 0 }).format(value);
    }

    cookieCountLabel(value) {
      return `${this.formatQty(value)} ${value === 1 ? 'cookie' : 'cookies'}`;
    }

    tierRange(tier) {
      const index = Math.max(0, this.tiers.findIndex((item) => item.min === tier.min));
      const next = this.tiers[index + 1];
      if (!next) return `${this.formatQty(tier.min)}+`;
      return `${this.formatQty(tier.min)}–${this.formatQty(next.min - 1)}`;
    }

    turnaroundFor(qty) {
      if (qty >= 1000) return { days: this.turnaroundDays.xl, range: '1,000+' };
      if (qty >= 500) return { days: this.turnaroundDays.large, range: '500–999' };
      if (qty >= 300) return { days: this.turnaroundDays.medium, range: '300–499' };
      return { days: this.turnaroundDays.small, range: '1–299' };
    }

    format(amount) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (_) {
        return `$${amount.toFixed(2)}`;
      }
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((el) => { el.textContent = value; });
    }

    updateShapeDimensions(size) {
      this.root.querySelectorAll('[data-lcb-shape-dimension]').forEach((el) => {
        const value = size === 'large' ? el.dataset.largeDimension : el.dataset.smallDimension;
        if (value) el.textContent = value;
      });
    }

    updateConditionals(packaging, border) {
      const ribbon = packaging === 'Individually wrapped and tied with ribbon';
      if (this.ribbonColorWrap) this.ribbonColorWrap.hidden = !ribbon;
      if (this.ribbonColor) {
        this.ribbonColor.required = ribbon;
        this.ribbonColor.disabled = !ribbon;
      }

      const withBorder = border === 'With border';
      if (this.borderColorWrap) this.borderColorWrap.hidden = !withBorder;
      if (this.borderColor) {
        this.borderColor.required = withBorder;
        this.borderColor.disabled = !withBorder;
      }
    }

    updateTierRows(tier, size) {
      this.root.querySelectorAll('[data-lcb-tier-row]').forEach((row) => {
        const current = parseInt(row.dataset.min, 10) === tier.min;
        row.classList.toggle('is-current', current);
        row.setAttribute('aria-pressed', current ? 'true' : 'false');
        const price = size === 'large' ? this.toMoney(row.dataset.large) : this.toMoney(row.dataset.small);
        const priceNode = row.querySelector('[data-lcb-tier-price]');
        if (priceNode) priceNode.textContent = this.format(price);
        const labelNode = row.querySelector('[data-lcb-tier-label]');
        const rowTier = this.tiers.find((item) => item.min === parseInt(row.dataset.min, 10));
        if (labelNode && rowTier) labelNode.textContent = this.tierRange(rowTier);
      });
      this.setText('[data-lcb-tier-size-label]', size === 'large' ? 'Large cookie' : 'Small cookie');
    }

    updateRange(qty) {
      if (!this.qtyRange || !this.tiers.length) return;
      const index = this.currentTierIndex(qty);
      this.qtyRange.value = index;
      const max = Math.max(1, this.tiers.length - 1);
      const progress = (index / max) * 100;
      const scale = this.qtyRange.closest('.lcb-range-scale');
      if (scale) scale.style.setProperty('--lcb-range-progress', `${progress}%`);
      this.root.querySelectorAll('[data-lcb-range-stop]').forEach((stop, stopIndex) => {
        stop.classList.toggle('is-passed', stopIndex < index);
        stop.classList.toggle('is-current', stopIndex === index);
      });
    }

    update() {
      const qty = this.quantity();
      const size = this.selected('Cookie Size', 'Small').toLowerCase();
      const packaging = this.selected('Packaging', 'Individually wrapped and heat sealed');
      const border = this.selected('Border', 'No border');
      const tier = this.currentTier(qty);

      if (this.qtyInput && parseInt(this.qtyInput.value, 10) < this.minimumQty()) this.qtyInput.value = this.minimumQty();

      const base = size === 'large' ? tier.large : tier.small;
      const packagingUpcharge = packaging === 'Individually wrapped and tied with ribbon' ? this.ribbonUpcharge : 0;
      const borderUpcharge = border === 'With border' ? this.borderUpcharge : 0;
      const unit = Math.max(0, base + packagingUpcharge + borderUpcharge);
      const line = unit * qty;
      const turnaround = this.turnaroundFor(qty);
      const tierLabel = this.tierRange(tier);

      this.updateConditionals(packaging, border);
      this.updateShapeDimensions(size);
      this.updateTierRows(tier, size);
      this.updateRange(qty);
      this.updateDeliveryDate(turnaround.days);

      this.setText('[data-lcb-unit-price]', this.format(unit));
      this.setText('[data-lcb-base-price]', this.format(base));
      this.setText('[data-lcb-upcharge-price]', this.format(packagingUpcharge + borderUpcharge));
      this.setText('[data-lcb-line-total]', this.format(line));
      this.setText('[data-lcb-atc-total]', this.format(line));
      this.setText('[data-lcb-current-tier]', `${tierLabel} cookies`);
      this.setText('[data-lcb-current-each]', `${this.format(base)} per cookie before extras`);
      this.setText('[data-lcb-turnaround-badge]', `${turnaround.days} business days`);
      this.setText('[data-lcb-turnaround-copy]', `Orders of ${turnaround.range} cookies have a standard production turnaround of ${turnaround.days} business days.`);
      if (this.atcLabel && !this.atcLabel.closest('button')?.disabled) {
        this.atcLabel.textContent = `Add ${this.cookieCountLabel(qty)} to cart`;
      }

      if (this.privateUnit) this.privateUnit.value = unit.toFixed(2);
      if (this.privateUnitCents) this.privateUnitCents.value = Math.round(unit * 100).toString();
      if (this.privateLine) this.privateLine.value = line.toFixed(2);
      if (this.privateTier) this.privateTier.value = tierLabel.replace('–', '-');
      if (this.privateTurnaround) this.privateTurnaround.value = `${turnaround.days} business days`;
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-logo-cookie-builder]:not([data-lcb-ready])').forEach((root) => {
      root.setAttribute('data-lcb-ready', 'true');
      new LogoCookieBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
